<script setup lang="ts">
  import { ref } from 'vue';

  import { ProtheusRecipientCombobox } from '@/entities/Protheus';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
  import { formatDateToPtBR } from '@/shared/lib';
  import { DatePicker, IconImage, NumberStepperInput } from '@/shared/ui';

  import {
    createPhysicalRecipientRow,
    type PhysicalRecipientOption,
    type PhysicalRecipientRow,
  } from '../model/physicalRecipient';

  const props = defineProps<{
    rows: PhysicalRecipientRow[];
    options: PhysicalRecipientOption[];
    recipientLabel: string;
    selectPlaceholder: string;
    searchPlaceholder: string;
    notFoundMessage: string;
    addButtonAlt: string;
    removeButtonAlt: string;
    groupTestId: string;
    rowTestId: string;
    dateButtonTestId: string;
    addButtonTestId: string;
    removeButtonTestId: string;
  }>();

  const emit = defineEmits<{
    (e: 'update:rows', value: PhysicalRecipientRow[]): void;
  }>();

  const formatSendDate = (value: Date | null) => {
    const formattedDate = formatDateToPtBR(value);
    return formattedDate === '' ? 'Selecione uma data' : formattedDate;
  };

  const updateRows = (updater: (rows: PhysicalRecipientRow[]) => PhysicalRecipientRow[]) => {
    emit('update:rows', updater(props.rows));
  };
  const updateRecipientId = (rowId: string, value: string) => {
    updateRows((rows) =>
      rows.map((row) => (row.id === rowId ? { ...row, recipientId: value } : row))
    );
  };

  const updateRecipientCopies = (rowId: string, value: number | null) => {
    updateRows((rows) =>
      rows.map((row) => (row.id === rowId ? { ...row, copies: value } : row))
    );
  };

  const isCopiesInvalid = (copies: number | null): boolean => copies === null || copies < 1;

  const pendingDateByRowId = ref<Record<string, Date | null>>({});
  const openRecipientDatePicker = (rowId: string) => {
    updateRows((rows) =>
      rows.map((row) => (row.id === rowId ? { ...row, isDatePickerOpen: true } : row))
    );
  };

  const setRecipientDatePickerOpen = (rowId: string, value: boolean) => {
    if (!value && rowId in pendingDateByRowId.value) {
      const pendingDate = pendingDateByRowId.value[rowId];
      const remaining = { ...pendingDateByRowId.value };
      delete remaining[rowId];
      pendingDateByRowId.value = remaining;
      updateRows((rows) =>
        rows.map((row) =>
          row.id === rowId ? { ...row, isDatePickerOpen: false, sendDate: pendingDate } : row
        )
      );
    } else {
      updateRows((rows) =>
        rows.map((row) => (row.id === rowId ? { ...row, isDatePickerOpen: value } : row))
      );
    }
  };

  const updateRecipientDate = (rowId: string, value: Date | null) => {
    pendingDateByRowId.value = { ...pendingDateByRowId.value, [rowId]: value };
  };

  const addRecipient = () => {
    updateRows((rows) => [...rows, createPhysicalRecipientRow()]);
  };

  const removeRecipient = (rowId: string) => {
    updateRows((rows) => rows.filter((row) => row.id !== rowId));
  };
</script>

<template>
  <div
    class="flex flex-col gap-4"
    :data-testid="groupTestId"
  >
    <div
      v-for="(row, index) in rows"
      :key="row.id"
      class="flex flex-wrap items-end gap-4 md:flex-nowrap"
      :data-testid="rowTestId"
    >
      <div class="flex w-full flex-col gap-2 md:w-[230px] md:min-w-[230px] md:flex-none">
        <label class="text-sm font-semibold text-gray-07">{{ recipientLabel }}</label>
        <ProtheusRecipientCombobox
          :model-value="row.recipientId"
          :options="options"
          :placeholder="selectPlaceholder"
          :search-placeholder="searchPlaceholder"
          :not-found-message="notFoundMessage"
          trigger-class="h-11 rounded-lg border-gray-02 bg-white-01 px-4"
          @update:model-value="updateRecipientId(row.id, $event)"
        />
      </div>

      <div class="flex w-[72px] flex-none flex-col gap-2">
        <label class="text-sm font-semibold text-gray-07">Cópias:</label>
        <NumberStepperInput
          :model-value="row.copies"
          :min="1"
          wrapper-class="w-[72px]"
          :input-class="`!h-11 !w-[72px] text-gray-03${isCopiesInvalid(row.copies) ? ' !border-red-16' : ''}`"
          @update:model-value="updateRecipientCopies(row.id, $event)"
        />
        <p
          v-if="isCopiesInvalid(row.copies)"
          class="text-xs text-red-16"
        >
          Mínimo 1.
        </p>
      </div>

      <div class="flex w-[180px] flex-none flex-col gap-2">
        <label class="text-sm font-semibold text-gray-07">Data de envio:</label>
        <button
          type="button"
          class="flex h-11 items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-4 py-3 text-sm text-gray-07 focus:border-gray-07 focus:outline-none"
          :data-testid="dateButtonTestId"
          @click="openRecipientDatePicker(row.id)"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
          />
          <span class="whitespace-nowrap">{{ formatSendDate(row.sendDate) }}</span>
        </button>
        <DatePicker
          :selected-date="row.sendDate"
          :is-open="row.isDatePickerOpen"
          :disable-future-dates="true"
          @update:is-open="setRecipientDatePickerOpen(row.id, $event)"
          @update:selected-date="updateRecipientDate(row.id, $event)"
        />
      </div>

      <div class="hidden flex-1 md:block" />

      <div
        class="flex h-11 w-6 flex-none items-center justify-end self-end text-[var(--theme-secondary-ink)]"
      >
        <button
          v-if="index === 0"
          type="button"
          class="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          :data-testid="addButtonTestId"
          @click="addRecipient"
        >
          <IconImage
            :src="AddButton"
            :alt="addButtonAlt"
            class="h-5 w-5"
          />
        </button>
        <button
          v-else
          type="button"
          class="cursor-pointer overflow-visible"
          :data-testid="removeButtonTestId"
          @click="removeRecipient(row.id)"
        >
          <IconImage
            :src="DeleteButton"
            :alt="removeButtonAlt"
            class="h-6 w-6"
          />
        </button>
      </div>
    </div>
  </div>
</template>
